import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Search,
    Filter,
    Calendar,
    User,
    ChevronDown,
    Download,
    FileText,
    History,
    Wallet,
    Info,
    AlertCircle,
    ArrowUpRight,
    ArrowDownRight,
    Clock,
    CheckCircle,
    XCircle,
    RotateCcw,
    Eye,
    TrendingUp,
    Award,
    RefreshCw,
    PlusCircle,
    Save,
    X,
    Upload, CalendarDays,
    ShieldCheck,
} from "lucide-react";

import { exportLeaveRequestsExcelRequest, createLeaveRequestRequest } from "../../../../redux/actions/leaveRequestsActions";

import {
    getAllCompanySoldesRequest,
    exportSoldesExcelRequest,
    importSoldesExcelRequest,
    updateSoldeRequest,
    getAllMovementsRequest
} from "../../../../redux/actions/leaveBalanceActions";
import { fetchParamCongesRequest } from "../../../../redux/actions/paramCongesActions";

import "../../Mesrequetes/MesrequetesStyles/DemandeConges.css";
import "../parametresStyles/ParamConges.css";
import "../parametresStyles/SoldeCongesManagement.css";

const STATIC_ANNUAL_LEAVE_SOLDES = [
    ["Jamila", "Boussaa"],
    ["jane", "smith"],
    ["farisi", "amina"],
    ["Omar", "Bennani"],
    ["Yassine", "El Haddad"],
    ["Amine", "Boukhari"],
    ["Ayoub", "Zouhair"],
    ["Meryem", "Fassi"],
    ["Younes", "Alaoui"],
    ["Nadia", "El Amrani"],
    ["Soufiane", "Belkhayat"],
    ["Houda", "Kabbaj"],
    ["Sara", "Ouazzani"],
    ["Nour", "Aït Lahcen"],
    ["Said", "Cherkaoui"],
    ["Khalid", "El Mansouri"],
    ["Ikram", "Touhami"],
    ["Anas", "Benali"],
    ["Amina", "Chafai"],
    ["Youssef", "Rahmani"],
    ["Salma", "El Harti"],
    ["Hamza", "Lamrani"],
    ["Karim", "Bouazza"],
    ["Hajar", "El Idrissi"],
    ["Fatimaezzahra", "ourabah"],
].map(([firstName, lastName], index) => ({
    id: `static-annual-${index + 1}`,
    userId: `static-user-${index + 1}`,
    userFullName: `${firstName} ${lastName}`,
    userEmployeeCode: `MAT${String(index + 1).padStart(4, "0")}`,
    absenceTypeId: "static-conge-annuel",
    absenceTypeNom: "Congé annuel",
    absenceTypeCode: "CA",
    absenceTypeCouleur: "#204385",
    annee: new Date().getFullYear(),
    soldeInitial: 18,
    soldeAcquis: 0,
    soldeConsomme: 0,
    soldeRestant: index === 0 ? 15 : 18,
    actif: true,
    isStatic: true,
}));

const SoldeCongesManagement = () => {
    const dispatch = useDispatch();
    const [staticSoldes, setStaticSoldes] = useState(STATIC_ANNUAL_LEAVE_SOLDES);

    // ── Redux state ──
    const allRequests = useSelector((state) => state.leaveRequests?.allRequests || []);
    const companySoldes = useSelector((state) => state.leaveBalance?.companySoldes || []);
    const movements = useSelector((state) => state.leaveBalance?.movements || []);
    const loading = useSelector((state) => state.leaveRequests?.loading || state.leaveBalance?.loading);

    const paramState = useSelector((state) => state.paramConges || {});
    const typesAbsences = paramState.typesAbsences || [];

    // ── UI State ──
    const [activeTab, setActiveTab] = useState("soldes");
    const [searchTerm, setSearchTerm] = useState("");
    const fileInputRef = useRef(null);

    // ── Adjustment Modal ──
    const [adjustModalOpen, setAdjustModalOpen] = useState(false);
    const [selectedSolde, setSelectedSolde] = useState(null);
    const [daysToAdd, setDaysToAdd] = useState("");

    // ── Saisir un congé Modal ──
    const [saisirModalOpen, setSaisirModalOpen] = useState(false);
    const [saisirForm, setSaisirForm] = useState({
        absenceTypeId: "",
        dateDebut: "",
        dateFin: "",
        commentaire: "",
        justificatif: null,
        autorisation: null,
        selectedUserId: "",
    });
    const [saisirError, setSaisirError] = useState("");
    const [userSearch, setUserSearch] = useState("");

    // ── Init ──
    useEffect(() => {
        dispatch(getAllCompanySoldesRequest());
        dispatch(getAllMovementsRequest());
        dispatch(fetchParamCongesRequest());
    }, [dispatch]);

    // ── Helpers ──
    const formatDate = (dateStr) => {
        if (!dateStr) return "-";
        return new Date(dateStr).toLocaleDateString("fr-FR", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
        });
    };

    const calculerDuree = (debut, fin) => {
        if (!debut || !fin) return 0;
        return Math.ceil((new Date(fin) - new Date(debut)) / (1000 * 60 * 60 * 24)) + 1;
    };

    const getAbsenceTypeLabel = (t) =>
        t?.label || t?.libelle || t?.name || t?.nom || "Type";

    const getAbsenceTypeColor = (t) =>
        t?.couleur || t?.color || "#3b82f6";

    // ── Types de congés disponibles ──
    const typesCongesDisponibles = useMemo(() => {
        return (typesAbsences || []).map((t) => ({
            id: t?.id,
            label: getAbsenceTypeLabel(t),
            couleur: getAbsenceTypeColor(t),
            requireJustificatif: !!t?.justificationRequired,
            justificatifMandatory: !!t?.justificationMandatory,
            justificationType: t?.justificationType || "Justificatif",
            requireAutorisation: !!t?.requireAutorisation || !!t?.isAutorisationRequired,
        }));
    }, [typesAbsences]);

    const saisirTypeObj = useMemo(() => {
        return typesCongesDisponibles.find(
            (t) => String(t.id) === String(saisirForm.absenceTypeId)
        ) || null;
    }, [saisirForm.absenceTypeId, typesCongesDisponibles]);

    // ── Utilisateurs uniques depuis companySoldes ──
    const uniqueUsers = useMemo(() => {
        const seen = new Set();
        return staticSoldes
            .filter((s) => {
                if (seen.has(s.userId)) return false;
                seen.add(s.userId);
                return true;
            })

            .map((s) => ({
                id: s.userId,
                fullName: s.userFullName,
                employeeCode: s.userEmployeeCode,
            }));
    }, [staticSoldes]);

    const filteredUsers = useMemo(() => {
        if (!userSearch) return uniqueUsers;
        const s = userSearch.toLowerCase();
        return uniqueUsers.filter(
            (u) =>
                u.fullName?.toLowerCase().includes(s) ||
                u.employeeCode?.toLowerCase().includes(s)
        );
    }, [uniqueUsers, userSearch]);

    // ── Export / Import ──
    const handleExport = () => {
        if (activeTab === "soldes") {
            dispatch(exportSoldesExcelRequest());
        } else {
            dispatch(exportLeaveRequestsExcelRequest("all_history"));
        }
    };

    const handleImportClick = () => fileInputRef.current?.click();

    const handleImportFile = (e) => {
        const file = e.target.files?.[0];
        if (file) {
            dispatch(importSoldesExcelRequest(file));
            e.target.value = "";
        }
    };

    // ── Adjustment Modal handlers ──
    const handleOpenAdjust = (solde) => {
        setSelectedSolde(solde);
        setDaysToAdd("");
        setAdjustModalOpen(true);
    };

    const handleCloseAdjust = () => {
        setAdjustModalOpen(false);
        setSelectedSolde(null);
    };

    const handleSaveAdjustment = () => {
        if (!selectedSolde || !daysToAdd) return;
        const add = parseFloat(daysToAdd);
        if (isNaN(add)) return;

        const currentAcquis = Number(selectedSolde.soldeAcquis || 0);
        const newAcquis = currentAcquis + add;
        if (newAcquis < 0) return;

        if (selectedSolde.isStatic) {
            setStaticSoldes((items) =>
                items.map((item) =>
                    item.id === selectedSolde.id
                        ? {
                            ...item,
                            soldeAcquis: newAcquis,
                            soldeRestant: Number(item.soldeInitial || 0) + newAcquis - Number(item.soldeConsomme || 0),
                        }
                        : item
                )
            );
            handleCloseAdjust();
            return;
        }

        dispatch(updateSoldeRequest(selectedSolde.id, {
            soldeAcquis: newAcquis,
            soldeConsomme: Number(selectedSolde.soldeConsomme || 0),
            actif: true
        }));
        handleCloseAdjust();
    };

    // ── Saisir un congé Modal handlers ──
    const handleCloseSaisir = () => {
        setSaisirModalOpen(false);
        setSaisirForm({
            absenceTypeId: "",
            dateDebut: "",
            dateFin: "",
            commentaire: "",
            justificatif: null,
            autorisation: null,
            selectedUserId: "",
        });
        setSaisirError("");
        setUserSearch("");
    };

    const handleSaisirSubmit = () => {
        setSaisirError("");

        if (!saisirForm.absenceTypeId) {
            setSaisirError("Veuillez sélectionner un type de congé.");
            return;
        }
        if (!saisirForm.dateDebut || !saisirForm.dateFin) {
            setSaisirError("Veuillez renseigner la date de début et la date de fin.");
            return;
        }
        if (!saisirForm.selectedUserId) {
            setSaisirError("Veuillez sélectionner un collaborateur.");
            return;
        }

        const payload = {
            absenceTypeId: Number(saisirForm.absenceTypeId),
            dateDebut: saisirForm.dateDebut,
            dateFin: saisirForm.dateFin,
            commentaire: saisirForm.commentaire,
            employeeId: saisirForm.selectedUserId, // L'ID du collaborateur bénéficiaire
            requestType: "conge", // Type par défaut pour ce module
            justificatif: saisirForm.justificatif,
            autorisation: saisirForm.autorisation,
        };

        dispatch(createLeaveRequestRequest(payload));
        handleCloseSaisir();
    };


    // ── Filtered data ──
    const historyMovements = useMemo(() => {
        let list = movements || [];
        if (searchTerm) {
            const s = searchTerm.toLowerCase();
            list = list.filter(
                (m) =>
                    (m.employeeName || "").toLowerCase().includes(s) ||
                    (m.employeeCode || "").toLowerCase().includes(s) ||
                    (m.absenceTypeNom || "").toLowerCase().includes(s) ||
                    (m.reference || "").toLowerCase().includes(s) ||
                    (m.typeMouvement || "").toLowerCase().includes(s)
            );
        }
        return list;
    }, [movements, searchTerm]);

    const combinedHistoryAll = useMemo(() => {
        // 1. Demandes approuvées / annulées / reportées de TOUS les collaborateurs
        const filteredRequests = (allRequests || []).filter((req) => {
            const s = (req.status || req.statut || "").toUpperCase();
            return (
                s === "APPROVED" || s === "APPROUVE" ||
                s === "CANCELLED" || s === "ANNULE" ||
                s === "POSTPONED" || s === "REPORTE"
            );
        });

        const requestsAsMovements = filteredRequests.map((req) => {
            const deb = req.dateDebut || req.startDate || req.fromDate;
            const fin = req.dateFin || req.endDate || req.toDate;
            const dateStr = `Du ${formatDate(deb)} au ${formatDate(fin)}`;
            const motifText = req.commentaire ? `${dateStr} - ${req.commentaire}` : dateStr;
            const valeur = -Number(req.duree || calculerDuree(deb, fin) || 0);

            const statusRaw = (req.status || req.statut || "").toUpperCase();
            const statut =
                statusRaw === "APPROVED" || statusRaw === "APPROUVE" ? "approuve" :
                    statusRaw === "CANCELLED" || statusRaw === "ANNULE" ? "annule" :
                        statusRaw === "POSTPONED" || statusRaw === "REPORTE" ? "reporte" : "approuve";

            const refText = req.demandeurName
                ? `${req.demandeurName} (${req.demandeurCode || "—"})`
                : `REF-${req.id}`;

            return {
                id: `req-${req.id}`,
                employeeName: req.employeeName,
                employeeCode: req.employeeCode,
                dateMouvement: req.createdAt || req.dateDemande || deb,
                reference: refText,
                motif: motifText,
                commentaire: req.motifRejet ? `Motif: ${req.motifRejet}` : "",
                absenceTypeNom: req.absenceTypeNom || req.absenceTypeName || "Congé",
                absenceTypeCouleur: req.absenceTypeCouleur || "#3b82f6",
                soldeInitialSnapshot: req.soldeInitial ?? null,
                soldeRestantSnapshot: req.soldeRestant ?? null,
                valeur,
                isLeaveRequest: true,
                statut,
            };
        });

        // 2. Mouvements positifs (crédits) de TOUS les collaborateurs
        const positiveMovements = (movements || [])
            .filter((m) => Number(m.valeur || 0) > 0)
            .map((m) => ({
                id: `mov-${m.id}`,
                employeeName: m.employeeName,
                employeeCode: m.employeeCode,
                dateMouvement: m.dateMouvement,
                reference: m.crediteurName
                    ? `${m.crediteurName} (${m.crediteurMatricule || "—"})`
                    : "SYS_AUTO",
                motif: m.commentaire || m.motif || "Ajustement",
                commentaire: "",
                absenceTypeNom: m.absenceTypeNom,
                absenceTypeCouleur: m.absenceTypeCouleur,
                soldeInitialSnapshot: m.soldeInitialSnapshot ?? null,
                soldeRestantSnapshot: m.soldeRestantSnapshot ?? null,
                valeur: Number(m.valeur || 0),
                isLeaveRequest: false,
                statut: "valide",
            }));

        // 3. Fusion + tri par date décroissante
        return [...(movements || []).map((m) => ({
            id: `mov-${m.id}`,
            employeeName: m.employeeName,
            employeeCode: m.employeeCode,
            dateMouvement: m.dateMouvement,
            reference: m.reference === "SYS_AUTO"
                ? "SYS_AUTO"
                : (m.crediteurName ? `${m.crediteurName} (${m.crediteurMatricule || "—"})` : (m.reference || "—")),
            motif: m.commentaire || m.motif || "Mouvement de solde",
            commentaire: "",
            absenceTypeNom: m.absenceTypeNom,
            absenceTypeCouleur: m.absenceTypeCouleur,
            soldeInitialSnapshot: m.soldeInitialSnapshot ?? null,
            soldeRestantSnapshot: m.soldeRestantSnapshot ?? null,
            valeur: Number(m.valeur || 0),
            isLeaveRequest: false,
            statut: m.typeMouvement || "valide",
        }))].sort((a, b) => {
            const da = a.dateMouvement ? new Date(a.dateMouvement).getTime() : 0;
            const db = b.dateMouvement ? new Date(b.dateMouvement).getTime() : 0;
            return db - da;
        });
    }, [movements]);

    const flattenedSoldes = useMemo(() => {
        let list = staticSoldes;
        if (searchTerm) {
            const s = searchTerm.toLowerCase();
            list = list.filter(
                (solde) =>
                    solde.userFullName?.toLowerCase().includes(s) ||
                    solde.userEmployeeCode?.toLowerCase().includes(s) ||
                    solde.absenceTypeNom?.toLowerCase().includes(s)
            );
        }
        return list;
    }, [staticSoldes, searchTerm]);

    const dureeCalculee = calculerDuree(saisirForm.dateDebut, saisirForm.dateFin);
    const selectedUser = uniqueUsers.find(
        (u) => String(u.id) === String(saisirForm.selectedUserId)
    );

    return (
        <div className="dcPage scmPage">
            <div className="dcWrap dcMax">

                {/* ── Header ── */}
                <div className="dcHeader scmHeader">
                    <div>
                        <h1>Gestion administrative des soldes de congés</h1>
                    </div>
                    <button
                        className="dcBtn dcBtn--primary scmPrimaryAction"
                        onClick={() => setSaisirModalOpen(true)}
                    >
                        <PlusCircle size={16} />
                        Saisir un congé
                    </button>
                </div>

                <div className="dcCard">
                    {/* ── Tabs ── */}
                    <div className="dcTabs">
                        <button
                            className={`dcTab ${activeTab === "historique" ? "is-active" : ""}`}
                            onClick={() => setActiveTab("historique")}
                        >
                            <History size={16} /> États des congés
                        </button>
                        <button
                            className={`dcTab ${activeTab === "soldes" ? "is-active" : ""}`}
                            onClick={() => setActiveTab("soldes")}
                        >
                            <Wallet size={16} /> Gestion des soldes de congés
                        </button>
                    </div>

                    <div className="dcBody">
                        <input
                            type="file"
                            ref={fileInputRef}
                            accept=".xlsx,.xls"
                            style={{ display: "none" }}
                            onChange={handleImportFile}
                        />

                        {/* ── Toolbar ── */}
                        <div className="scmToolbar">
                            <div className="scmSearch">
                                <Search size={16} />
                                <input
                                    type="text"
                                    placeholder="Rechercher par collaborateur, matricule ou type..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="dcInput"
                                />
                            </div>
                            <div className="scmActions">
                                <button
                                    className="dcBtn dcBtn--outline"
                                    onClick={handleExport}
                                    disabled={loading}
                                    style={{
                                        background: activeTab === "historique" ? "#10b981" : "",
                                        color: activeTab === "historique" ? "white" : "",
                                        border: activeTab === "historique" ? "none" : "",
                                    }}
                                >
                                    <Download size={15} /> Exporter Excel
                                </button>
                                {activeTab === "soldes" && (
                                    <button
                                        className="dcBtn dcBtn--primary"
                                        onClick={handleImportClick}
                                        disabled={loading}
                                    >
                                        <ArrowUpRight size={15} /> Importer Excel
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* ── Tab: Historique ── */}
                        {activeTab === "historique" && (
                            <div className="dcTableWrap">
                                <table className="dcTable">
                                    <thead>
                                    <tr>
                                        <th>Référence</th>
                                        <th>Date de l'opération</th>
                                        <th>Motif</th>
                                        <th>Type de congé</th>
                                        <th>Solde débit</th>
                                        <th>Solde crédit</th>
                                        <th>Solde</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {loading ? (
                                        <tr>
                                            <td colSpan={8}>
                                                <div className="dcEmpty"><Clock size={24}/><div>Chargement...</div></div>
                                            </td>
                                        </tr>
                                    ) : combinedHistoryAll.length === 0 ? (
                                        <tr>
                                            <td colSpan={8}>
                                                <div className="dcEmpty"><AlertCircle size={24}/><div>Aucun mouvement trouvé</div></div>
                                            </td>
                                        </tr>
                                    ) : (
                                        combinedHistoryAll.map((item, index) => {
                                            const isPositive = item.valeur > 0;
                                            const isNegative = item.valeur < 0;
                                            const isCancelledOrRefused = item.isLeaveRequest &&
                                                (item.statut === "annule" || item.statut === "refuse");

                                            const statusBadge = item.isLeaveRequest ? {
                                                label:
                                                    item.statut === "approuve" ? "Approuvé" :
                                                        item.statut === "refuse"   ? "Refusé"   :
                                                            item.statut === "annule"   ? "Annulé"   :
                                                                item.statut === "reporte"  ? "Reporté"  : item.statut,
                                                bg:
                                                    item.statut === "approuve" ? "#dcfce7" :
                                                        item.statut === "refuse"   ? "#fee2e2" :
                                                            item.statut === "annule"   ? "#f3f4f6" :
                                                                item.statut === "reporte"  ? "#dbeafe" : "#f3f4f6",
                                                color:
                                                    item.statut === "approuve" ? "#16a34a" :
                                                        item.statut === "refuse"   ? "#ef4444" :
                                                            item.statut === "annule"   ? "#6b7280" :
                                                                item.statut === "reporte"  ? "#2563eb" : "#6b7280",
                                            } : null;

                                            return (
                                                <tr
                                                    key={item.id || index}
                                                    style={{
                                                        backgroundColor: isCancelledOrRefused ? "#f9fafb"
                                                            : isPositive  ? "#f0fdf4"
                                                                : isNegative  ? "#fef2f2" : "#fff",
                                                        borderLeft: isCancelledOrRefused ? "3px solid #d1d5db"
                                                            : isPositive  ? "3px solid #16a34a"
                                                                : isNegative  ? "3px solid #ef4444"
                                                                    : "3px solid #6b7280",
                                                        opacity: isCancelledOrRefused ? 0.55 : 1,
                                                    }}
                                                >
                                                    <td>
                                                        <div style={{
                                                            fontWeight: 700,
                                                            color: "#15803d",
                                                            textDecoration: isCancelledOrRefused ? "line-through" : "none"
                                                        }}>
                                                            {item.employeeName || "—"}
                                                        </div>
                                                        <div style={{ fontSize: "11px", color: "#6b7280" }}>
                                                            {item.employeeCode || "—"}
                                                        </div>
                                                    </td>
                                                    <td>{formatDate(item.dateMouvement)}</td>
                                                    <td style={{ fontSize: "12px", color: "#4b5563", maxWidth: "220px" }}>
                                                        <div style={{
                                                            fontWeight: "bold",
                                                            color: "#111827",
                                                            display: "flex",
                                                            alignItems: "center",
                                                            flexWrap: "wrap",
                                                            gap: "6px"
                                                        }}>
                                    <span style={{
                                        textDecoration: isCancelledOrRefused ? "line-through" : "none"
                                    }}>
                                        {item.reference}
                                    </span>
                                                            {statusBadge && (
                                                                <span style={{
                                                                    fontSize: "10px",
                                                                    padding: "1px 6px",
                                                                    borderRadius: "4px",
                                                                    fontWeight: 600,
                                                                    display: "inline-block",
                                                                    backgroundColor: statusBadge.bg,
                                                                    color: statusBadge.color,
                                                                }}>
                                            {statusBadge.label}
                                        </span>
                                                            )}
                                                        </div>
                                                        <div>{item.motif || item.commentaire || "—"}</div>
                                                    </td>
                                                    <td>
                                                        <div className="dcTypeCell">
                                    <span
                                        className="dcDot"
                                        style={{ backgroundColor: item.absenceTypeCouleur || (isPositive ? "#16a34a" : "#ef4444") }}
                                    />
                                                            <span>{item.absenceTypeNom || "—"}</span>
                                                        </div>
                                                    </td>
                                                    <td style={{
                                                        textAlign: "center",
                                                        color: "#ef4444",
                                                        fontWeight: 700,
                                                        textDecoration: isCancelledOrRefused ? "line-through" : "none"
                                                    }}>
                                                        {isNegative ? item.valeur + "j" : "—"}
                                                    </td>
                                                    <td style={{ textAlign: "center", fontWeight: 700, color: "#16a34a" }}>
                                                        {isPositive ? "+" + item.valeur + "j" : "—"}
                                                    </td>
                                                    <td style={{ textAlign: "center", fontWeight: 800, background: "#f8fafc" }}>
                                                        {item.soldeRestantSnapshot !== null && item.soldeRestantSnapshot !== undefined
                                                            ? item.soldeRestantSnapshot + "j"
                                                            : "—"}
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    )}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {/* ── Tab: Mouvements ── */}
                        {activeTab === "mouvements" && (
                            <div className="dcTableWrap">
                                <table className="dcTable">
                                    <thead>
                                    <tr>
                                        <th>Collaborateur</th>
                                        <th>Matricule</th>
                                        <th>Date</th>
                                        <th>Type Congé</th>
                                        <th>Opération</th>
                                        <th style={{textAlign: "center"}}>Valeur</th>
                                        <th style={{textAlign: "center"}}>Nouveau Solde</th>
                                        <th>Référence / Commentaire</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {loading ? (
                                        <tr>
                                            <td colSpan="8">
                                                <div className="dcEmpty"><Clock size={24}/>
                                                    <div>Chargement...</div>
                                                </div>
                                            </td>
                                        </tr>
                                    ) : historyMovements.length === 0 ? (
                                        <tr>
                                            <td colSpan="8">
                                                <div className="dcEmpty"><AlertCircle size={24}/>
                                                    <div>Aucun mouvement trouvé</div>
                                                </div>
                                            </td>
                                        </tr>
                                    ) : (
                                        historyMovements.map((move) => {
                                            const val = move.valeur || 0;
                                            const isPositive = val > 0;
                                            const isNegative = val < 0;

                                            const getTypeColor = (type) => {
                                                switch (type) {
                                                    case "ACCRUAL":
                                                        return "#10b981";
                                                    case "CONSUMPTION":
                                                        return "#ef4444";
                                                    case "CANCELLATION":
                                                        return "#3b82f6";
                                                    case "INITIAL":
                                                        return "#8b5cf6";
                                                    case "ADJUSTMENT":
                                                        return "#f59e0b";
                                                    default:
                                                        return "#6b7280";
                                                }
                                            };

                                            const getTypeLabel = (type) => {
                                                switch (type) {
                                                    case "ACCRUAL":
                                                        return "Acquisition";
                                                    case "CONSUMPTION":
                                                        return "Consommation";
                                                    case "CANCELLATION":
                                                        return "Restitution";
                                                    case "INITIAL":
                                                        return "Initialisation";
                                                    case "ADJUSTMENT":
                                                        return "Ajustement";
                                                    default:
                                                        return type;
                                                }
                                            };

                                            return (
                                                <tr key={move.id}>
                                                    <td>
                                                        <div className="scmEmpCell">
                                                            <div
                                                                className="scmAvatar">{move.employeeName?.charAt(0)}</div>
                                                            <div className="scmEmpName">{move.employeeName}</div>
                                                        </div>
                                                    </td>
                                                    <td>{move.employeeCode}</td>
                                                    <td style={{ fontSize: "13px" }}>{new Date(move.dateMouvement).toLocaleString()}</td>
                                                    <td>
                                                            <span
                                                                className="dcBadge"
                                                                style={{
                                                                    background: `${move.absenceTypeCouleur}20`,
                                                                    color: move.absenceTypeCouleur,
                                                                    border: `1px solid ${move.absenceTypeCouleur}40`,
                                                                }}
                                                            >
                                                                {move.absenceTypeNom}
                                                            </span>
                                                    </td>
                                                    <td>
                                                            <span style={{ color: getTypeColor(move.typeMouvement), fontWeight: 600, fontSize: "12px" }}>
                                                                {getTypeLabel(move.typeMouvement)}
                                                            </span>
                                                    </td>
                                                    <td style={{ textAlign: "center", fontWeight: "bold", color: isPositive ? "#10b981" : isNegative ? "#ef4444" : "inherit" }}>
                                                        {isPositive ? "+" : ""}{val}j
                                                    </td>
                                                    <td style={{ textAlign: "center", fontWeight: "500" }}>{move.soldeRestantSnapshot}j</td>
                                                    <td style={{ fontSize: "12px", color: "#6b7280" }}>
                                                        <div style={{ fontWeight: "500", color: "#374151" }}>{move.reference}</div>
                                                        <div>{move.commentaire}</div>
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    )}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {/* ── Tab: Soldes ── */}
                        {activeTab === "soldes" && (
                            <div className="dcTableWrap">
                                <table className="dcTable">
                                    <thead>
                                    <tr>
                                        <th>Collaborateur</th>
                                        <th>Matricule</th>
                                        <th>Type de Congé</th>
                                        <th style={{ textAlign: "center" }}>Solde crédit</th>
                                        <th style={{ textAlign: "center" }}>Solde</th>
                                        <th style={{ textAlign: "center" }}>Actions</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {loading ? (
                                        <tr>
                                            <td colSpan="6">
                                                <div className="dcEmpty"><Clock size={24} /><div>Chargement...</div></div>
                                            </td>
                                        </tr>
                                    ) : flattenedSoldes.length === 0 ? (
                                        <tr>
                                            <td colSpan="6">
                                                <div className="dcEmpty"><AlertCircle size={24} /><div>Aucun solde trouvé</div></div>
                                            </td>
                                        </tr>
                                    ) : (
                                        flattenedSoldes.map((solde, idx) => (
                                            <tr key={idx}>
                                                <td>
                                                    <div className="scmEmpCell">
                                                        <div className="scmAvatar">{solde.userFullName?.charAt(0)}</div>
                                                        <div className="scmEmpName">{solde.userFullName}</div>
                                                    </div>
                                                </td>
                                                <td>{solde.userEmployeeCode}</td>
                                                <td>
                                                    <div className="dcTypeCell">
                                                        <span className="dcDot" style={{ backgroundColor: solde.absenceTypeCouleur || "#2563eb" }} />
                                                        <span>{solde.absenceTypeNom}</span>
                                                    </div>
                                                </td>
                                                <td style={{ textAlign: "center" }}>
                                                        <span className="scmTotalSolde" style={{ color: "#2563eb" }}>
                                                            {solde.soldeAcquis || 0} <span className="dcTiny">jours</span>
                                                        </span>
                                                </td>
                                                <td style={{ textAlign: "center" }}>
                                                        <span className="scmTotalSolde" style={{ color: solde.soldeRestant < 0 ? "#ef4444" : "#16a34a" }}>
                                                            {solde.soldeRestant} <span className="dcTiny">jours</span>
                                                        </span>
                                                </td>
                                                <td style={{ textAlign: "center" }}>
                                                    <button
                                                        className="dcBtn dcBtn--icon dcBtn--primary"
                                                        style={{ padding: "6px", borderRadius: "8px" }}
                                                        onClick={() => handleOpenAdjust(solde)}
                                                        title="Ajouter des jours"
                                                    >
                                                        <PlusCircle size={18} />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>

                {/* ══════════════════════════════════════════
                    MODALE : Ajustement du solde
                ══════════════════════════════════════════ */}
                {adjustModalOpen && (
                    <div className="tnfModal">
                        <div className="tnfModal__panel tnfModal__panel--small" role="dialog" aria-modal="true">
                            <div className="tnfModal__header">
                                <div className="tnfModal__headLeft">
                                    <div className="tnfModal__icon">
                                        <PlusCircle size={18} />
                                    </div>
                                    <div>
                                        <div className="tnfModal__title">Ajustement du solde</div>
                                        <div className="tnfModal__sub">
                                            {selectedSolde?.absenceTypeNom} — {selectedSolde?.annee}
                                        </div>
                                    </div>
                                </div>
                                <button className="tnfIconBtn" type="button" onClick={handleCloseAdjust}>
                                    <X size={18} />
                                </button>
                            </div>

                            <div className="tnfModal__body">
                                <div className="tnfPanel" style={{ marginBottom: 20 }}>
                                    <div className="tnfPanel__title">
                                        <User size={16} /> Collaborateur
                                    </div>
                                    <div className="tnfKv">
                                        <span>Nom</span>
                                        <b>{selectedSolde?.userFullName}</b>
                                    </div>
                                    <div className="tnfKv">
                                        <span>Matricule</span>
                                        <b>{selectedSolde?.userEmployeeCode}</b>
                                    </div>
                                    <div className="tnfKv">
                                        <span>Type de congé</span>
                                        <b>{selectedSolde?.absenceTypeNom}</b>
                                    </div>
                                    <div className="tnfKv">
                                        <span>Solde restant</span>
                                        <b style={{ color: selectedSolde?.soldeRestant < 0 ? "#ef4444" : "#16a34a" }}>
                                            {selectedSolde?.soldeRestant} jours
                                        </b>
                                    </div>
                                </div>

                                <div className="tnfPanel">
                                    <div className="tnfPanel__title">
                                        <TrendingUp size={16} /> Ajustement
                                    </div>
                                    <label className="tnfLabel" style={{ display: "block", marginBottom: 6 }}>
                                        Nombre de jours à ajouter
                                    </label>
                                    <div style={{ position: "relative" }}>
                                        <input
                                            type="number"
                                            className="dcInput"
                                            style={{ width: "100%", paddingRight: 48 }}
                                            placeholder="Ex: 1.5, -2, ..."
                                            value={daysToAdd}
                                            onChange={(e) => setDaysToAdd(e.target.value)}
                                            autoFocus
                                        />
                                        <div style={{
                                            position: "absolute", right: 12, top: "50%",
                                            transform: "translateY(-50%)", color: "#9ca3af", fontSize: 12
                                        }}>
                                            jours
                                        </div>
                                    </div>
                                    <div className="tnfHelp" style={{ marginTop: 6 }}>
                                        Utilisez une valeur négative pour retirer des jours
                                    </div>
                                </div>
                            </div>

                            <div className="tnfModal__footer">
                                <button className="tnfBtn tnfBtn--ghost" type="button" onClick={handleCloseAdjust}>
                                    Annuler
                                </button>
                                <button
                                    className="tnfBtn tnfBtn--ok"
                                    type="button"
                                    onClick={handleSaveAdjustment}
                                    disabled={!daysToAdd}
                                >
                                    <Save size={16} /> Enregistrer
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* ══════════════════════════════════════════
                    MODALE : Saisir un congé
                ══════════════════════════════════════════ */}
                {saisirModalOpen && (
                    <div className="tnfModal">
                        <div
                            className="tnfModal__panel"
                            role="dialog"
                            aria-modal="true"
                            style={{ maxWidth: 620, maxHeight: "90vh", display: "flex", flexDirection: "column" }}
                        >
                            <div className="tnfModal__header">
                                <div className="tnfModal__headLeft">
                                    <div className="tnfModal__icon">
                                        <PlusCircle size={18} />
                                    </div>
                                    <div>
                                        <div className="tnfModal__title">Saisir un congé</div>
                                        <div className="tnfModal__sub">Enregistrer un congé pour un collaborateur</div>
                                    </div>
                                </div>
                                <button className="tnfIconBtn" type="button" onClick={handleCloseSaisir}>
                                    <X size={18} />
                                </button>
                            </div>

                            <div className="tnfModal__body" style={{ overflowY: "auto", flex: 1 }}>

                                {saisirError && (
                                    <div className="dcAlert" style={{ marginBottom: 16 }}>
                                        <AlertCircle size={16} color="#9f1239" />
                                        <div style={{ fontSize: 13 }}>{saisirError}</div>
                                    </div>
                                )}

                                {/* Détails du congé */}
                                <div className="tnfPanel" style={{ marginBottom: 16 }}>
                                    <div className="tnfPanel__title">
                                        <CalendarDays size={16} /> Détails du congé
                                    </div>

                                    <label className="tnfLabel" style={{ display: "block", marginBottom: 6 }}>
                                        Type de congé <span className="tnfReq">*</span>
                                    </label>
                                    <select
                                        className="dcInput"
                                        style={{ width: "100%", marginBottom: 14 }}
                                        value={saisirForm.absenceTypeId}
                                        onChange={(e) => setSaisirForm({
                                            ...saisirForm,
                                            absenceTypeId: e.target.value,
                                            justificatif: null,
                                            autorisation: null,
                                        })}
                                    >
                                        <option value="">Sélectionner un type</option>
                                        {typesCongesDisponibles.map((t) => (
                                            <option key={t.id} value={t.id}>{t.label}</option>
                                        ))}
                                    </select>

                                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
                                        <div>
                                            <label className="tnfLabel" style={{ display: "block", marginBottom: 6 }}>
                                                Date de début <span className="tnfReq">*</span>
                                            </label>
                                            <input
                                                type="date"
                                                className="dcInput"
                                                style={{ width: "100%" }}
                                                value={saisirForm.dateDebut}
                                                onChange={(e) => setSaisirForm({ ...saisirForm, dateDebut: e.target.value })}
                                            />
                                        </div>
                                        <div>
                                            <label className="tnfLabel" style={{ display: "block", marginBottom: 6 }}>
                                                Date de fin <span className="tnfReq">*</span>
                                            </label>
                                            <input
                                                type="date"
                                                className="dcInput"
                                                style={{ width: "100%" }}
                                                value={saisirForm.dateFin}
                                                onChange={(e) => setSaisirForm({ ...saisirForm, dateFin: e.target.value })}
                                            />
                                        </div>
                                    </div>

                                    {saisirForm.dateDebut && saisirForm.dateFin && dureeCalculee > 0 && (
                                        <div className="tnfMiniCard" style={{ marginBottom: 14 }}>
                                            <div className="tnfMiniRow">
                                                <span className="tnfMuted">Durée calculée</span>
                                                <b className="tnfAccent">{dureeCalculee} jour{dureeCalculee > 1 ? "s" : ""}</b>
                                            </div>
                                        </div>
                                    )}

                                    <label className="tnfLabel" style={{ display: "block", marginBottom: 6 }}>
                                        Commentaire / Motif
                                    </label>
                                    <textarea
                                        className="tnfTextarea"
                                        placeholder="Précisez le motif du congé..."
                                        value={saisirForm.commentaire}
                                        onChange={(e) => setSaisirForm({ ...saisirForm, commentaire: e.target.value })}
                                        rows={3}
                                    />
                                </div>

                                {/* Justificatif conditionnel */}
                                {saisirTypeObj?.requireJustificatif && (
                                    <div className="tnfPanel" style={{ marginBottom: 16 }}>
                                        <div className="tnfPanel__title">
                                            <FileText size={16} /> Documents
                                        </div>
                                        <label className="tnfLabel" style={{ display: "block", marginBottom: 8 }}>
                                            {saisirTypeObj.justificationType}
                                            {saisirTypeObj.justificatifMandatory && <span className="tnfReq"> *</span>}
                                        </label>
                                        <div style={{
                                            border: "2px dashed #d1d5db", borderRadius: 10,
                                            padding: 16, textAlign: "center", background: "#f9fafb"
                                        }}>
                                            <input
                                                type="file" id="saisir-justif"
                                                accept=".pdf,.jpg,.jpeg,.png"
                                                style={{ display: "none" }}
                                                onChange={(e) => setSaisirForm({ ...saisirForm, justificatif: e.target.files?.[0] || null })}
                                            />
                                            <label htmlFor="saisir-justif" className="tnfBtn tnfBtn--ghost" style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6 }}>
                                                <Upload size={14} /> Parcourir
                                            </label>
                                            {saisirForm.justificatif && (
                                                <div style={{ marginTop: 8, fontSize: 12, color: "#16a34a", fontWeight: 600 }}>
                                                    ✓ {saisirForm.justificatif.name}
                                                </div>
                                            )}
                                            <div className="tnfHelp" style={{ marginTop: 6 }}>PDF, JPG, PNG (max 5Mo)</div>
                                        </div>
                                    </div>
                                )}

                                {/* Autorisation conditionnelle */}
                                {saisirTypeObj?.requireAutorisation && (
                                    <div className="tnfPanel" style={{ marginBottom: 16 }}>
                                        <div className="tnfPanel__title">
                                            <FileText size={16} /> Autorisation
                                        </div>
                                        <div style={{
                                            border: "2px dashed #fca5a5", borderRadius: 10,
                                            padding: 16, textAlign: "center", background: "#fff5f5"
                                        }}>
                                            <input
                                                type="file" id="saisir-auto"
                                                accept=".pdf,.jpg,.jpeg,.png"
                                                style={{ display: "none" }}
                                                onChange={(e) => setSaisirForm({ ...saisirForm, autorisation: e.target.files?.[0] || null })}
                                            />
                                            <label htmlFor="saisir-auto" className="tnfBtn tnfBtn--ghost" style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6, borderColor: "#ef4444", color: "#ef4444" }}>
                                                <Upload size={14} /> Parcourir
                                            </label>
                                            {saisirForm.autorisation && (
                                                <div style={{ marginTop: 8, fontSize: 12, color: "#16a34a", fontWeight: 600 }}>
                                                    ✓ {saisirForm.autorisation.name}
                                                </div>
                                            )}
                                            <div className="tnfHelp" style={{ marginTop: 6 }}>PDF, JPG, PNG (max 5Mo)</div>
                                        </div>
                                    </div>
                                )}

                                {/* Sélection collaborateur */}
                                <div className="tnfPanel">
                                    <div className="tnfPanel__title">
                                        <User size={16} /> Collaborateur <span className="tnfReq">*</span>
                                    </div>

                                    {selectedUser && (
                                        <div className="tnfMiniCard" style={{ marginBottom: 12, borderColor: "#204385", background: "#eff6ff" }}>
                                            <div className="tnfMiniRow">
                                                <span className="tnfMuted">Sélectionné</span>
                                                <b style={{ color: "#204385", display: "flex", alignItems: "center", gap: 6 }}>
                                                    <CheckCircle size={14} color="#204385" />
                                                    {selectedUser.fullName} — {selectedUser.employeeCode}
                                                </b>
                                            </div>
                                        </div>
                                    )}

                                    <div className="tnfHelp" style={{ marginBottom: 12, display: "flex", gap: 6, alignItems: "start", color: "#64748b" }}>
                                        <ShieldCheck size={14} style={{ flexShrink: 0, marginTop: 2 }} />
                                        <span>Seuls les collaborateurs ayant activé la délégation dans leurs paramètres sont visibles ici.</span>
                                    </div>

                                    <div style={{ position: "relative", marginBottom: 10 }}>
                                        <Search size={14} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#9ca3af" }} />
                                        <input
                                            type="text"
                                            className="dcInput"
                                            placeholder="Rechercher un collaborateur..."
                                            value={userSearch}
                                            onChange={(e) => setUserSearch(e.target.value)}
                                            style={{ width: "100%", paddingLeft: 32, fontSize: 13 }}
                                        />
                                    </div>

                                    <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 200, overflowY: "auto" }}>
                                        {filteredUsers.length === 0 ? (
                                            <div style={{ textAlign: "center", color: "#9ca3af", fontSize: 12, padding: "16px 8px", lineHeight: "1.5" }}>
                                                {userSearch
                                                    ? "Aucun collaborateur trouvé"
                                                    : "Aucun collaborateur n'a encore activé la délégation de saisie de congés."}
                                            </div>

                                        ) : (
                                            filteredUsers.map((user) => {
                                                const isSelected = String(saisirForm.selectedUserId) === String(user.id);
                                                return (
                                                    <div
                                                        key={user.id}
                                                        onClick={() => setSaisirForm({ ...saisirForm, selectedUserId: user.id })}
                                                        style={{
                                                            display: "flex", alignItems: "center", gap: 10,
                                                            padding: "10px 12px", borderRadius: 10,
                                                            border: `2px solid ${isSelected ? "#204385" : "#e5e7eb"}`,
                                                            background: isSelected ? "#eff6ff" : "#fff",
                                                            cursor: "pointer", transition: "all 0.15s ease",
                                                        }}
                                                    >
                                                        <div style={{
                                                            width: 34, height: 34, borderRadius: "50%",
                                                            background: isSelected ? "#204385" : "#e5e7eb",
                                                            color: isSelected ? "#fff" : "#374151",
                                                            display: "flex", alignItems: "center", justifyContent: "center",
                                                            fontWeight: 700, fontSize: 13, flexShrink: 0,
                                                        }}>
                                                            {user.fullName?.charAt(0)?.toUpperCase()}
                                                        </div>
                                                        <div style={{ flex: 1 }}>
                                                            <div style={{ fontWeight: 600, fontSize: 13, color: "#111827" }}>{user.fullName}</div>
                                                            <div style={{ fontSize: 11, color: "#6b7280" }}>{user.employeeCode}</div>
                                                        </div>
                                                        {isSelected && <CheckCircle size={16} color="#204385" />}
                                                    </div>
                                                );
                                            })
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="tnfModal__footer">
                                {saisirForm.selectedUserId && saisirForm.absenceTypeId && saisirForm.dateDebut && saisirForm.dateFin && (
                                    <div style={{ flex: 1, fontSize: 12, color: "#6b7280", display: "flex", alignItems: "center", gap: 6 }}>
                                        <CheckCircle size={14} color="#10b981" />
                                        {selectedUser?.fullName} · {typesCongesDisponibles.find(t => String(t.id) === String(saisirForm.absenceTypeId))?.label} · {dureeCalculee}j
                                    </div>
                                )}
                                <button className="tnfBtn tnfBtn--ghost" type="button" onClick={handleCloseSaisir}>
                                    Annuler
                                </button>
                                <button
                                    className="tnfBtn tnfBtn--ok"
                                    type="button"
                                    onClick={handleSaisirSubmit}
                                    disabled={
                                        !saisirForm.absenceTypeId ||
                                        !saisirForm.dateDebut ||
                                        !saisirForm.dateFin ||
                                        !saisirForm.selectedUserId
                                    }
                                >
                                    <Save size={16} /> Enregistrer
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SoldeCongesManagement;
